import { Button } from '@/components/ui'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import type { StaffRole } from '@/auth/storage'
import { ClubSwitcher } from './ClubSwitcher'
import { LicenseBanner } from '@/components/license'

interface NavItem {
  to: string
  label: string
  icon: React.JSX.Element
  /** если задано — пункт виден только этим ролям (super_admin видит всё) */
  roles?: StaffRole[]
}

interface NavGroup {
  /** подпись группы; null — без подписи (верхний блок) */
  label: string | null
  items: NavItem[]
}

const ROLE_LABEL: Record<StaffRole, string> = {
  super_admin: 'Супер-админ',
  owner: 'Владелец',
  admin: 'Администратор',
  viewer: 'Наблюдатель',
  accountant: 'Бухгалтер',
  // Кросс-клубные роли Help (SupportHub): заводятся через канал интеграции,
  // а не руками, но в шапке подписываются как обычные.
  support: 'Поддержка',
  manager: 'Менеджер'
}

/**
 * Ограниченные роли: перечисленная здесь роль видит ТОЛЬКО свои разделы, всё
 * остальное скрыто из меню и закрыто гвардами роутов (см. App.tsx — иначе раздел
 * открывался бы по прямому URL). Роли, которых в карте нет, фильтруются по
 * item.roles, как раньше; super_admin видит всё.
 *
 * «Бухгалтер» — клубная роль: дашборд, отчёты, финансы, брони, смены, журнал.
 * SMS он видит потому, что превышение бесплатного порога попадает в счёт: без
 * доступа к расходу не проверить, откуда взялась строка. Рассылки и проверку
 * шлюза ему не показывают — см. фильтр вкладок в pages/Sms.tsx.
 *
 * support и manager — кросс-клубные роли Help (club_id = null). Набор повторяет
 * то, что разрешает бэкенд (@Roles в clubs/leads/finance-контроллерах): support —
 * клубы с их подписками, устройствами и сотрудниками плюс заявки; manager —
 * только счета и акты. Клубные разделы им не показываем вообще: своего клуба у
 * них нет, а бэкенд на такие запросы отвечает 400/403.
 */
const ROLE_PATHS: Partial<Record<StaffRole, ReadonlySet<string>>> = {
  accountant: new Set([
    '/',
    '/support',
    '/analytics',
    '/reports',
    '/finance',
    '/reconciliation',
    '/payroll',
    '/bookings',
    '/shifts',
    '/journal',
    '/sms'
  ]),
  support: new Set(['/clubs', '/leads']),
  manager: new Set(['/finance'])
  // Сотрудникам Help (support/manager) раздел «Поддержка» не нужен: они и есть
  // поддержка, а писать самим себе из чужого бекофиса — бессмыслица.
}

// Иконки — лёгкий inline-SVG, без зависимостей.
const Icon = {
  dashboard: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  ),
  analytics: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M4 20V4" strokeLinecap="round" />
      <path d="M4 20h16" strokeLinecap="round" />
      <path d="M8 20v-6M12 20V8M16 20v-9M20 20V5" strokeLinecap="round" />
    </svg>
  ),
  reports: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M8 21v-6M16 21v-9M12 21v-4" strokeLinecap="round" />
    </svg>
  ),
  clubs: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M3 21h18M5 21V8l5-4 5 4M15 21V11l4-2.5V21" />
      <path d="M9 21v-4h2v4" strokeLinecap="round" />
    </svg>
  ),
  settings: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-2.7-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 4.6 15H4.5a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.1-2.7l-.1-.1A2 2 0 1 1 8.4 5.4l.1.1A1.6 1.6 0 0 0 11 4.6V4.5a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.1a2 2 0 1 1 0 4h-.3z" />
    </svg>
  ),
  support: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M12 13v.01M12 7a2 2 0 0 1 1 3.8c-.6.35-1 .9-1 1.7" strokeLinecap="round" />
    </svg>
  ),
  logout: (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" strokeLinecap="round" />
      <path d="M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  tariffs: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M20 12 12 4H6a2 2 0 0 0-2 2v6l8 8 8-8z" strokeLinejoin="round" />
      <circle cx="8.5" cy="8.5" r="1.4" />
    </svg>
  ),
  tables: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="3" y="5" width="18" height="12" rx="2" />
      <path d="M6 17v2M18 17v2" strokeLinecap="round" />
      <circle cx="12" cy="11" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  ),
  loyalty: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path
        d="M12 21s-7-4.35-9-8C1.4 9.5 3 6 6.5 6 9 6 11 8 12 9c1-1 3-3 5.5-3C21 6 22.6 9.5 21 13c-2 3.65-9 8-9 8z"
        strokeLinejoin="round"
      />
    </svg>
  ),
  customers: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20a6 6 0 0 1 12 0" strokeLinecap="round" />
      <path d="M16 5.5a3 3 0 0 1 0 5.8M18 20a6 6 0 0 0-3-5.2" strokeLinecap="round" />
    </svg>
  ),
  users: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <circle cx="12" cy="9" r="2.4" />
      <path d="M8 16a4 4 0 0 1 8 0" strokeLinecap="round" />
    </svg>
  ),
  team: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19a6 6 0 0 1 12 0" strokeLinecap="round" />
      <circle cx="17.5" cy="9.5" r="2.2" />
      <path d="M16 14a5 5 0 0 1 5 5" strokeLinecap="round" />
    </svg>
  ),
  shifts: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  journal: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 8h6M9 12h6M9 16h4" strokeLinecap="round" />
    </svg>
  ),
  inventory: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M3 7l9-4 9 4-9 4-9-4z" strokeLinejoin="round" />
      <path d="M3 7v10l9 4 9-4V7" strokeLinejoin="round" />
      <path d="M12 11v10" />
    </svg>
  ),
  bar: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M8 2h8l1 7H7L8 2z" strokeLinejoin="round" />
      <path d="M7 9c0 5 1 9 5 11 4-2 5-6 5-11" strokeLinejoin="round" />
      <path d="M5 9h14" />
      <path d="M15 4h3" strokeLinecap="round" />
    </svg>
  ),
  telegram: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M21 4L3 11l6 2 2 6 3-4 4 3 3-14z" strokeLinejoin="round" />
    </svg>
  ),
  sms: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-4 3v-3H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" strokeLinejoin="round" />
      <path d="M7.5 11h.01M12 11h.01M16.5 11h.01" strokeLinecap="round" />
    </svg>
  ),
  bookings: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" strokeLinecap="round" />
      <path d="M9 14l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  licenses: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 10h5M7 14h3" strokeLinecap="round" />
      <circle cx="17" cy="12" r="2.5" />
      <path d="M17 14.5v3" strokeLinecap="round" />
    </svg>
  ),
  finance: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M6 3h9l3 3v15H6z" strokeLinejoin="round" />
      <path d="M9 9h6M9 13h6M9 17h4" strokeLinecap="round" />
    </svg>
  ),
  leads: (
    <svg
      className="nav-link__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M3 12l3-7h12l3 7v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6z" strokeLinejoin="round" />
      <path d="M3 12h5l1 2h6l1-2h5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const NAV: NavGroup[] = [
  {
    label: null,
    items: [
      { to: '/', label: 'Дашборд', icon: Icon.dashboard },
      { to: '/analytics', label: 'Аналитика', icon: Icon.analytics },
      { to: '/reports', label: 'Отчёты', icon: Icon.reports },
      { to: '/sales', label: 'Продажи', icon: Icon.reports },
      { to: '/finance', label: 'Финансы', icon: Icon.finance },
      { to: '/reconciliation', label: 'Сверка', icon: Icon.finance },
      { to: '/payroll', label: 'Зарплата', icon: Icon.finance, roles: ['owner', 'admin', 'accountant'] },
      { to: '/bookings', label: 'Бронирования', icon: Icon.bookings },
      { to: '/sms', label: 'SMS', icon: Icon.sms },
      { to: '/shifts', label: 'Смены', icon: Icon.shifts },
      { to: '/journal', label: 'Журнал', icon: Icon.journal },
      { to: '/team', label: 'Сотрудники БО', icon: Icon.team, roles: ['owner'] },
      { to: '/clubs', label: 'Клубы', icon: Icon.clubs, roles: ['super_admin'] },
      { to: '/leads', label: 'Заявки', icon: Icon.leads, roles: ['super_admin'] },
      { to: '/licenses', label: 'Лицензии', icon: Icon.licenses, roles: ['super_admin'] }
    ]
  },
  {
    label: 'Конфигурация',
    items: [
      { to: '/tariffs', label: 'Тарифы', icon: Icon.tariffs },
      { to: '/tables', label: 'Столы', icon: Icon.tables },
      { to: '/loyalty', label: 'Лояльность', icon: Icon.loyalty },
      { to: '/customers', label: 'Клиенты', icon: Icon.customers },
      { to: '/inventory', label: 'Склад', icon: Icon.inventory },
      { to: '/menu', label: 'Меню бара', icon: Icon.bar },
      { to: '/warehouse', label: 'Склад бара и кухни', icon: Icon.inventory },
      { to: '/devices', label: 'Устройства', icon: Icon.tables },
      { to: '/users', label: 'Пользователи', icon: Icon.users },
      { to: '/settings', label: 'Настройки', icon: Icon.settings },
      { to: '/telegram', label: 'Телеграм-бот', icon: Icon.telegram },
      { to: '/support', label: 'Поддержка', icon: Icon.support }
    ]
  }
]

const TITLES: Record<string, string> = {
  '/': 'Дашборд',
  '/analytics': 'Аналитика',
  '/reports': 'Отчёты',
  '/finance': 'Финансы',
  '/reconciliation': 'Сверка фискализации',
  '/payroll': 'Зарплата',
  '/bookings': 'Бронирования',
  '/sms': 'SMS',
  '/shifts': 'Смены',
  '/journal': 'Журнал',
  '/team': 'Сотрудники БО',
  '/clubs': 'Клубы',
  '/leads': 'Заявки',
  '/licenses': 'Лицензии',
  '/tariffs': 'Тарифы',
  '/tables': 'Столы',
  '/loyalty': 'Лояльность',
  '/customers': 'Клиенты',
  '/inventory': 'Склад',
  '/sales': 'Продажи',
  '/menu': 'Меню бара',
  '/warehouse': 'Склад бара и кухни',
  '/devices': 'Устройства',
  '/users': 'Пользователи',
  '/settings': 'Настройки',
  '/telegram': 'Телеграм-бот',
  '/support': 'Поддержка'
}

export function AppLayout(): React.JSX.Element {
  const { staff, claims, isSuperAdmin, logout } = useAuth()
  const location = useLocation()

  const role = claims?.role
  const canSee = (item: NavItem): boolean => {
    if (isSuperAdmin) return true
    const allowed = role ? ROLE_PATHS[role] : undefined
    if (allowed) return allowed.has(item.to)
    return !item.roles || (role ? item.roles.includes(role) : false)
  }
  const groups = NAV.map((g) => ({ ...g, items: g.items.filter(canSee) })).filter(
    (g) => g.items.length > 0
  )
  const title = TITLES[location.pathname] ?? 'Бекофис'
  const displayName = staff?.name || staff?.email || 'Сотрудник'
  const roleLabel = role ? ROLE_LABEL[role] : ''

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <div className="sidebar__brand-mark">8</div>
          <div className="sidebar__brand-text">
            <span className="sidebar__brand-title">8Ball</span>
            <span className="sidebar__brand-sub">Бекофис</span>
          </div>
        </div>

        <nav className="sidebar__nav">
          {groups.map((group, gi) => (
            <div className="nav-group" key={group.label ?? `g${gi}`}>
              {group.label && <div className="nav-group__label">{group.label}</div>}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar__footer">
          8Ball POS · multi-tenant
          <br />
          Фаза A — каркас
        </div>
      </aside>

      <div className="main">
        <LicenseBanner />
        <header className="header">
          <h1 className="header__title">{title}</h1>
          <div className="header__user">
            {/* Быстрый вход в поддержку из любого раздела: когда что-то не
                работает, искать пункт в боковом меню — последнее, чем хочется
                заниматься. */}
            <NavLink to="/support" className="btn btn--ghost btn--sm" title="Написать в поддержку">
              {Icon.support}
              <span>Поддержка</span>
            </NavLink>
            <ClubSwitcher />
            <div className="header__user-meta">
              <div className="header__user-name">{displayName}</div>
              <div className="header__user-role">{roleLabel}</div>
            </div>
            <Button variant="ghost" onClick={logout} title="Выйти">
              {Icon.logout}
              <span>Выйти</span>
            </Button>
          </div>
        </header>

        <main className="content">
          <div className="content__inner">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
